/** Reads an uploaded sheet the way the server will, for the upload step's
 *  preview: which sheet holds the data, where its header row is, and which
 *  rows count. TS port of discovr's internal/campaigns/import_sheet_parse.go
 *  (readXLSXRows, findHeaderRow, countDataRows; decodeCSVText, sniffDelimiter,
 *  readCSVRows for a .csv). Keep the two in sync by hand. */
import type { CellObject, WorkSheet } from 'xlsx';
import { stripInvisibleChars } from './invisibleChars';
import { rowHasKnownHeader } from './sheetHeaderAliases';

/** How many non-blank rows from the top are searched for the header. */
export const MAX_HEADER_SCAN_ROWS = 10;
/** How many non-blank lines are read to pick a CSV's delimiter. */
const MAX_SNIFF_LINES = 10;
const CSV_DELIMITERS = [',', ';', '\t'] as const;
const MIN_SPLIT_FIELDS = 2;
const CSV_CONTENT_TYPES = new Set(['text/csv', 'application/csv']);
const CSV_EXTENSION = '.csv';

export interface PreviewSheet {
  header: string[];
  /** Every non-blank row under the header. */
  dataRows: string[][];
}

export function isBlankRow(row: readonly string[]): boolean {
  return row.every((cell) => cell.trim() === '');
}

/** Same test the server applies to an upload: a CSV content type or name. */
export function isCsvFile(file: { name: string; type: string }): boolean {
  return CSV_CONTENT_TYPES.has(file.type.trim().toLowerCase()) || file.name.toLowerCase().endsWith(CSV_EXTENSION);
}

function startsWithBytes(bytes: Uint8Array, prefix: number[]): boolean {
  return prefix.every((byte, i) => bytes[i] === byte);
}

const CONTINUATION_MIN = 0x80;
const CONTINUATION_MAX = 0xbf;

/** The allowed range of the byte after a multi-byte lead, as Go's
 *  utf8.DecodeRune checks it: E0 allows A0-BF, ED 80-9F, F0 90-BF and F4
 *  80-8F, which rules out overlong forms, surrogates and values past U+10FFFF. */
function secondByteRange(lead: number): readonly [number, number] {
  if (lead === 0xe0) return [0xa0, CONTINUATION_MAX];
  if (lead === 0xed) return [CONTINUATION_MIN, 0x9f];
  if (lead === 0xf0) return [0x90, CONTINUATION_MAX];
  if (lead === 0xf4) return [CONTINUATION_MIN, 0x8f];
  return [CONTINUATION_MIN, CONTINUATION_MAX];
}

function sequenceLength(lead: number): number {
  if (lead >= 0xc2 && lead <= 0xdf) return 2;
  if (lead >= 0xe0 && lead <= 0xef) return 3;
  if (lead >= 0xf0 && lead <= 0xf4) return 4;
  return 0;
}

function isValidSequenceAt(bytes: Uint8Array, start: number): boolean {
  const lead = bytes[start] ?? 0;
  const length = sequenceLength(lead);
  if (length === 0) return false;
  const [min, max] = secondByteRange(lead);
  const second = bytes[start + 1];
  if (second === undefined || second < min || second > max) return false;
  return Array.from({ length: length - 2 }, (_, k) => bytes[start + 2 + k]).every(
    (byte) => byte !== undefined && byte >= CONTINUATION_MIN && byte <= CONTINUATION_MAX,
  );
}

/** Whether the bytes hold a valid multi-byte UTF-8 sequence, which a
 *  single-byte legacy file practically never does. */
function hasMultiByteUtf8(bytes: Uint8Array): boolean {
  return bytes.some((_, i) => isValidSequenceAt(bytes, i));
}

/** A CSV's text: a byte-order mark picks UTF-8 or UTF-16 and is dropped; bytes
 *  that are not valid UTF-8 read as Windows-1252 (a Windows "CSV" save) unless
 *  the file holds a real multi-byte UTF-8 sequence, which keeps its text. */
export function decodeCsvText(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  if (startsWithBytes(bytes, [0xff, 0xfe])) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  if (startsWithBytes(bytes, [0xfe, 0xff])) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  const body = startsWithBytes(bytes, [0xef, 0xbb, 0xbf]) ? bytes.subarray(3) : bytes;
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(body);
  } catch {
    return new TextDecoder(hasMultiByteUtf8(body) ? 'utf-8' : 'windows-1252').decode(body);
  }
}

/** Splits CSV text into records the way Go's encoding/csv does with lazy
 *  quotes: a quote opens a field only at its start, a doubled quote inside is
 *  a literal, and a lone quote that does not end the field stays in it. */
function parseCsv(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let atFieldStart = true;
  const endField = () => {
    row.push(field);
    field = '';
    atFieldStart = true;
  };
  const endRow = () => {
    endField();
    rows.push(row);
    row = [];
  };
  for (let i = 0; i < text.length; i += 1) {
    const char = text.charAt(i);
    const next = text.charAt(i + 1);
    if (inQuotes) {
      if (char !== '"') field += char;
      else if (next === '"') {
        field += '"';
        i += 1;
      } else if (next === '' || next === delimiter || next === '\n' || (next === '\r' && text.charAt(i + 2) === '\n')) inQuotes = false;
      else field += char;
    } else if (char === '"' && atFieldStart) {
      inQuotes = true;
      atFieldStart = false;
    } else if (char === delimiter) endField();
    else if (char === '\n') endRow();
    else if (char === '\r' && next === '\n') {
      i += 1;
      endRow();
    } else {
      field += char;
      atFieldStart = false;
    }
  }
  if (field !== '' || row.length > 0) endRow();
  return rows;
}

function sniffLines(text: string): string[] {
  const lines: string[] = [];
  let start = 0;
  while (start <= text.length && lines.length < MAX_SNIFF_LINES) {
    const end = text.indexOf('\n', start);
    const line = text.slice(start, end < 0 ? text.length : end).replace(/\r+$/, '');
    if (line.replace(/[,;\t]/g, '').trim() !== '') lines.push(line);
    if (end < 0) break;
    start = end + 1;
  }
  return lines;
}

/** How many lines split into the field count (above one) most lines share. */
function mostCommonSplit(lines: string[], delimiter: string): number {
  const byFields = new Map<number, number>();
  let most = 0;
  for (const line of lines) {
    const fields = parseCsv(line, delimiter)[0]?.length ?? 0;
    if (fields < MIN_SPLIT_FIELDS) continue;
    const count = (byFields.get(fields) ?? 0) + 1;
    byFields.set(fields, count);
    most = Math.max(most, count);
  }
  return most;
}

/** The delimiter that splits the most of the first non-blank lines into the
 *  same field count, so a lone title line with a comma cannot outvote a
 *  semicolon header and its rows. A tie goes to the comma. */
export function sniffDelimiter(text: string): string {
  const lines = sniffLines(text);
  let best: string = CSV_DELIMITERS[0];
  let bestLines = 0;
  for (const delimiter of CSV_DELIMITERS) {
    const agreeing = mostCommonSplit(lines, delimiter);
    if (agreeing > bestLines) {
      best = delimiter;
      bestLines = agreeing;
    }
  }
  return best;
}

/** The cells of a CSV with their invisible characters removed, like the
 *  server's read (stripInvisibleCells): a zero-width space is not a value. */
export function csvRows(text: string): string[][] {
  return parseCsv(text, sniffDelimiter(text)).map((row) => row.map(stripInvisibleChars));
}

function nonBlankCount(rows: string[][]): number {
  return rows.filter((row) => !isBlankRow(row)).length;
}

/** The first sheet with at least two non-blank rows (a header and something
 *  under it), so a workbook opening on a cover sheet still shows its data. A
 *  workbook with no such sheet falls back to the first one holding a single
 *  row: the server refuses it, but the preview can still show its header.
 *  Hidden rows count, as they do on the server. */
export function chooseDataSheet<T extends string[][]>(sheets: Iterable<T>): T | null {
  let headerOnly: T | null = null;
  for (const rows of sheets) {
    const count = nonBlankCount(rows);
    if (count >= 2) return rows;
    if (count === 1 && headerOnly === null) headerOnly = rows;
  }
  return headerOnly;
}

/** Rows read from a workbook: `hidden` holds the indexes of the rows the
 *  workbook hides. They stay in the list because the server looks at them to
 *  pick the data sheet and, as a last resort, the header, but they are never
 *  data rows. */
export type PreviewRows = string[][] & { hidden?: ReadonlySet<number> };

const NO_HIDDEN_ROWS: ReadonlySet<number> = new Set();

/** The top non-blank rows searched for a known column name; also notes the
 *  first non-blank row. Hidden rows are skipped unless `withHidden`. */
function scanHeaderRows(
  rows: string[][],
  hidden: ReadonlySet<number>,
  withHidden: boolean,
  isKnownHeader: (row: string[]) => boolean,
): { known: number; first: number } {
  let first = -1;
  let scanned = 0;
  for (const [i, row] of rows.entries()) {
    if (isBlankRow(row) || (hidden.has(i) && !withHidden)) continue;
    if (scanned === MAX_HEADER_SCAN_ROWS) break;
    if (first < 0) first = i;
    scanned += 1;
    if (isKnownHeader(row)) return { known: i, first };
  }
  return { known: -1, first };
}

/** The index of the header row, or -1 for a sheet with no non-blank row. It is
 *  the first of the top non-blank rows to hold a known column name; failing
 *  that, the first non-blank row. Visible rows are searched first; a hidden
 *  row is the header only when no visible row names a known column. Mirrors
 *  the server's findHeaderRow. */
export function findHeaderRow(
  rows: string[][],
  isKnownHeader: (row: string[]) => boolean,
  hidden: ReadonlySet<number> = NO_HIDDEN_ROWS,
): number {
  const visible = scanHeaderRows(rows, hidden, false, isKnownHeader);
  if (visible.known >= 0 || hidden.size === 0) return visible.known >= 0 ? visible.known : visible.first;
  const all = scanHeaderRows(rows, hidden, true, isKnownHeader);
  if (all.known >= 0) return all.known;
  return visible.first >= 0 ? visible.first : all.first;
}

/** The header and the non-blank, visible rows under it; rows above the header
 *  are ignored. */
export function splitAtHeader(rows: PreviewRows, knownNames: ReadonlySet<string>): PreviewSheet | null {
  const hidden = rows.hidden ?? NO_HIDDEN_ROWS;
  const headerIndex = findHeaderRow(rows, (row) => rowHasKnownHeader(row, knownNames), hidden);
  const header = rows[headerIndex];
  if (header === undefined) return null;
  const dataRows = rows.filter((row, i) => i > headerIndex && !hidden.has(i) && !isBlankRow(row));
  return { header, dataRows };
}

/** The SheetJS helpers the sheet reader needs, passed in so the xlsx library
 *  itself stays a lazy import in the caller. */
export interface SheetJsHelpers {
  utils: {
    decode_range: (ref: string) => { s: { r: number; c: number }; e: { r: number; c: number } };
    encode_cell: (address: { r: number; c: number }) => string;
  };
}

/** Options that make SheetJS keep each cell's number format (`z`) and the
 *  hidden flags of rows and columns. */
export const WORKBOOK_READ_OPTIONS = { type: 'array', cellNF: true, cellStyles: true } as const;

const PERCENT_SCALE = 100;
const MAX_SIGNIFICANT_DIGITS = 15;
const MAX_FRACTION_DIGITS = 20;
const ROUNDING_SLOP = 1e-9;

const STORED_NUMBER = /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/;
const DISPLAYED_NUMBER = /^-?\d+(\.\d+)?$/;

/** A number as plain digits: 15 significant digits, no exponent, and none of
 *  the float noise a multiplication leaves behind (0.07 * 100 is
 *  7.000000000000001). Mirrors the backend's plainNumber. */
export function plainNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  const rounded = Number(n.toPrecision(MAX_SIGNIFICANT_DIGITS));
  if (rounded === 0) return '0';
  return rounded.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: MAX_FRACTION_DIGITS });
}

/** Replaces each quoted string and escaped character by one marker, so
 *  literal text can neither pose as a date token nor as a digit placeholder. */
function maskQuoted(code: string): string {
  return code.replace(/"[^"]*"|[\\_*]./g, '\u0001');
}

/** A bracket that makes the display something other than the number: a
 *  condition ([=1]) or an elapsed-time bracket ([h]). Colours and currency or
 *  locale tags ([Red], [$-409]) are not. */
function isBracketCondition(inner: string): boolean {
  if (inner === '') return false;
  if ('=<>'.includes(inner.charAt(0))) return true;
  return /^[hms]+$/.test(inner);
}

/** Whether a number format shows the number itself. A date or time token
 *  (d m y h s), a condition, a text-only section or a text format (@) means
 *  the display is something else and the cell keeps its displayed text.
 *  Mirrors the backend's numericFormatCode. */
export function isNumericFormat(code: string): boolean {
  const masked = maskQuoted(code.toLowerCase());
  const brackets = [...masked.matchAll(/\[([^\]]*)\]/g)];
  if (brackets.some((match) => isBracketCondition(match[1] ?? ''))) return false;
  if (/\[[^\]]*$/.test(masked)) return false;
  const flat = masked.replace(/\[[^\]]*\]/g, '');
  if (/[dmyhs]/.test(flat)) return false;
  let numericSection = false;
  for (const raw of flat.split(';')) {
    const section = raw.trim();
    if (section === '' || section.includes('@')) continue;
    if (section.includes('general') || /[0#?]/.test(section)) numericSection = true;
    else return false;
  }
  return numericSection;
}

function decimalPlaces(digits: string): number {
  const dot = digits.indexOf('.');
  return dot < 0 ? 0 : digits.length - dot - 1;
}

/** Whether a percent-formatted cell shows its stored value times 100 (0.045
 *  as 4.5%, the usual case) rather than the stored value itself (4.5 behind
 *  a literal "%" in the format). Mirrors the backend's percentOfStored. */
function percentOfStored(v: number, shown: string): boolean {
  const digits = shown.replace(/[, ]/g, '');
  if (!DISPLAYED_NUMBER.test(digits)) return true;
  const n = Number(digits);
  const tolerance = 0.5 * 10 ** -decimalPlaces(digits) + ROUNDING_SLOP;
  return Math.abs(n - v * PERCENT_SCALE) <= tolerance || Math.abs(n - v) > tolerance;
}

/** A cell's text the way the server reads it, from the number Excel stores
 *  (`raw`) and the text it displays (`formatted`): a plain number (`numeric`)
 *  is its stored value (a "12,500" or "12.5K" display is 12500), a percent
 *  shows as the stored value times 100 with a "%" (0.045 displayed as 4.5%
 *  reads "4.5%"), and text, dates, booleans and errors keep what the sheet
 *  displays. Mirrors the backend's storedCellValue. */
export function storedCellValue(raw: string, formatted: string, numeric: boolean): string {
  if (!numeric || raw === formatted || !STORED_NUMBER.test(raw)) return formatted;
  const v = Number(raw);
  if (!Number.isFinite(v)) return formatted;
  const trimmed = formatted.trim();
  if (!trimmed.endsWith('%')) return plainNumber(v);
  const shown = trimmed.slice(0, -1);
  return percentOfStored(v, shown) ? `${plainNumber(v * PERCENT_SCALE)}%` : `${plainNumber(v)}%`;
}

function cellText(cell: CellObject | undefined): string {
  return stripInvisibleChars(rawCellText(cell));
}

function rawCellText(cell: CellObject | undefined): string {
  if (cell === undefined || cell.t === 'z') return '';
  if (cell.t !== 'n' || typeof cell.v !== 'number') return cell.w ?? String(cell.v ?? '');
  // A built-in format SheetJS has no code for (Thai dates, ids 71-81) comes
  // through without a string format; the server keeps the displayed text for
  // it, so the preview does too.
  const numeric = typeof cell.z === 'string' && isNumericFormat(cell.z);
  return storedCellValue(String(cell.v), cell.w ?? '', numeric);
}

/** A worksheet as rows of text, with its blank rows dropped and its hidden
 *  columns left out (the server does not import them). Hidden rows stay in the
 *  list and are flagged in `hidden` (see PreviewRows). */
export function worksheetRows(sheet: WorkSheet, helpers: SheetJsHelpers): PreviewRows {
  const rows: PreviewRows = [];
  const ref = sheet['!ref'];
  if (ref === undefined) return rows;
  const range = helpers.utils.decode_range(ref);
  const rowSettings = sheet['!rows'] ?? [];
  const hiddenCols = sheet['!cols'] ?? [];
  const columns = Array.from({ length: range.e.c - range.s.c + 1 }, (_, i) => range.s.c + i).filter((c) => hiddenCols[c]?.hidden !== true);
  const hidden = new Set<number>();
  for (let r = range.s.r; r <= range.e.r; r += 1) {
    const cells = columns.map((c) => cellText(sheet[helpers.utils.encode_cell({ r, c })] as CellObject | undefined));
    if (isBlankRow(cells)) continue;
    if (rowSettings[r]?.hidden === true) hidden.add(rows.length);
    rows.push(cells);
  }
  rows.hidden = hidden;
  return rows;
}
